import React, { memo, useCallback, useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ContentContainer from "../../templates/content-container/contentContainer";
import ContentContainerToolbar from "../../ui-components/molecules/content-container-toolbar/contentContainerToolbar";
import getTerminalSummaryColumns from "./terminalSummaryColumns";
import "./terminalSummary.scss";
import UserState from "../../slices/user/UserState";
import { userSelector } from "../../slices/user/userSlice";
import XGSErrorMessage from "../../ui-components/error-message/errorMessage";
import Table from "../../ui-components/table/table";
import { PAGE_IDS } from "../../app/data/common/pageIds";
import TerminalSummaryInfo from "../../ui-components/map/terminalsummary-tooltip/terminalSummaryTooltip";
import Button, { ButtonThemes, ButtonSizes } from "../../ui-components/button/button";
import { LabelModes } from "../../ui-components/molecules/labeled-inputs/labeledInput";
import LabeledTextInput from "../../ui-components/molecules/labeled-inputs/labeled-text-input/labeledTextInput";
import LabeledSelectInput from "../../ui-components/molecules/labeled-inputs/labeled-select-input/labeledSelectInput";
import { TerminalSummaryCount } from "./termina-summary-tile/terminalSummary";
import { TerminalTileLoadingStatusState } from "../../slices/terminal-summary/terminalSummaryTiles/terminalTilesState";
import { getTerminalTilesLoadingStatus, terminalTileLoadingStatusSelector } from "../../slices/terminal-summary/terminalSummaryTiles/terminalTilesLoadingStatusSlice";
import TerminalSummaryState from "../../slices/terminal-summary/TerminalSummaryState";
import { ReactComponent as TerminalActivityIcon } from "../../images/TerminalAtivityLog.svg";
import TerminalActivityLog from "../company/terminal-activitylog/terminalActivityLog";
import { useHistory } from 'react-router-dom';
import { resetTerminalDetails, terminalDetailsSelector } from "slices/terminal-summary/terminalDetailsSlice";
import { ReactComponent as TerminalMarker } from "../../images/marker.svg";

import {
  terminalSummarySelector,
  getTerminalsList,
  clearTerminalSummaryList,
  setTerminalSummaryRequest
} from "../../slices/terminal-summary/terminalSummarySlice";
import { XGSSelectOption } from "../../ui-components/xgs-select/xgsSelect";
import {
  serviceCentersSelector,
  getRegionsServiceCenters
} from "../../slices/service-centers/serviceCentersSlice";
import ServiceCentersState from "../../slices/service-centers/ServiceCentersState";
import AddEditTerminal from "./addEditTerminal/addEditTerminal";
import { TerminalState } from "slices/terminal-summary/TerminalDetailsState";
import { toast } from "react-toastify";
import { UserUtils } from "app/data/user/userUtils";
import { statusOptions } from "app/data/terminal-details/models";
import { Popover } from "ui-components/popover/popover";
import { UpdateOriginalCountsState } from "slices/terminal-summary/terminalSummaryTiles/OriginalCountsState";
import { getOriginalCounts, originalCountsSelector } from "slices/terminal-summary/terminalSummaryTiles/originalCountsSlice";
import marker from "../../images/marker-red.svg";
import markerLightBlue from "../../images/marker-light-blue.svg";
import markerDarkBlue from "../../images/marker-dark-blue.svg";
import markerYellow from "../../images/marker-yellow.svg";
import starWhite from "../../images/star-outline.svg";
import Collapsible from "ui-components/collapsible/collapsible";
import XGSLeafletMap from "ui-components/map/leaflet/XGSLeafletMap";
import { generateFacilityPinSvg } from "ui-components/map/custom-marker/generateFacilityPin";
import { LeafletMarkerModel } from "ui-components/map/leaflet/xgsLeafletMap.types";

const terminalTypes = [
  {
    key: 'distributor',
    label: 'XGS Terminal',
    tooltip:
      'Official XGS Distributor (Company) location offering curbside delivery — shipments are dropped off at the curb, front door, or loading dock. Movement beyond the drop-off point is the recipient’s responsibility.',
  },
  {
    key: 'agent',
    label: 'Agent Terminal',
    tooltip:
      'Third-party delivery partner operating on behalf of XGS.',
  },
  {
    key: 'RLM',
    label: 'WG Location',
    tooltip:
      'Terminal that provides inside delivery — shipments are moved beyond the curb and brought into the building (e.g., dock, foyer, or designated indoor area), including navigation of ramps or stairs as needed.',
  },
];

const TerminalSummary: React.FC<{}> = memo((props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const userState: UserState = useSelector(userSelector);
  const terminalSummaryState: TerminalSummaryState = useSelector(terminalSummarySelector);
  const [region, setRegion] = useState<XGSSelectOption | null | undefined>(null);
  const [status, setStatus] = useState<XGSSelectOption | null | undefined>({
    label: "Active",
    value: "ACTIVE"
  });
  const [terminalType, setTerminalType] = useState<string | null | undefined>(null);
  const [physicalLocation, setPhysicalLocation] = useState<boolean | null | undefined>(null);
  const [clickedStates, setClickedStates] = useState({
    xgs: false,
    agents: false,
    rlm: false,
  });
  const [searchString, setSearchString] = useState<string>("");
  const [activityLogId, setActivityLogId] = useState<string | undefined>();
  const serviceCentersState: ServiceCentersState = useSelector(serviceCentersSelector);
  const terminalDetailsState: TerminalState = useSelector(terminalDetailsSelector);
  const originalCountsState: UpdateOriginalCountsState = useSelector(originalCountsSelector);

  const [slideOpenAddTerminal, setSlideOpenAddTerminal] = useState<boolean>(false);

  const onInfiniteScroll = () => {
    if (
      terminalSummaryState.fetchPortionStarted ||
      terminalSummaryState.terminalSummaryRequest.last
    ) return;

    dispatch(getTerminalsList((terminalSummaryState.terminalSummaryRequest.page ?? 0) + 1,
      searchString, region?.label ?? null, status?.value ?? "ACTIVE", terminalType ?? null, physicalLocation ?? null));
  };

  const terminalTileLoadingStatusState: TerminalTileLoadingStatusState = useSelector(terminalTileLoadingStatusSelector);

  //get tiles list
  useEffect(() => {
    const userTerminal = userState.activeTerminal?.id;
    if (userTerminal) {
      clear();
      dispatch(getTerminalTilesLoadingStatus());
      dispatch(getRegionsServiceCenters());
      dispatch(getTerminalsList(0, searchString ?? null, region ?? null, status?.value ?? "ACTIVE", terminalType ?? null, physicalLocation ?? null));
      if (typeof status?.value === "string") {
        dispatch(getOriginalCounts(status?.value));
      }
      else {
        dispatch(getOriginalCounts("ACTIVE"));
      }
    }
    // eslint-disable-next-line
  }, [userState.activeTerminal]);


  const getRegionsOptions = () => {
    return (
      serviceCentersState.regions?.map(
        (region): XGSSelectOption => ({
          label: region.regionName,
          value: region.id
        })
      ) || []
    );
  };

  const onRegionChanged = (_region: XGSSelectOption | null | undefined) => {
    if (!_region) return;
    setRegion(_region);
    dispatch(getTerminalsList(0, searchString, _region.label, status?.value, terminalType ?? null, physicalLocation ?? null));
  };

  const onStatusChanged = (_status: XGSSelectOption | null | undefined) => {
    if (!_status) return;
    setStatus(_status);
    dispatch(getTerminalsList(0, searchString, region?.label, _status.value, terminalType ?? null, physicalLocation ?? null));
    if (typeof _status.value === "string") {
      dispatch(getOriginalCounts(_status.value));
    }
    else {
      dispatch(getOriginalCounts("ACTIVE"));
    }
  };

  const onSearchStart = useCallback(() => {
    dispatch(clearTerminalSummaryList());
    dispatch(setTerminalSummaryRequest({ page: 0, last: false }));
    dispatch(getTerminalsList(0, searchString, region?.label ?? null, status?.value ?? "ACTIVE", terminalType ?? null, physicalLocation ?? null));
  }, [searchString, region?.label, status?.value, terminalType, physicalLocation, dispatch]);

  const clear = () => {
    setRegion(null);
    setSearchString("");
    setStatus({
      label: "Active",
      value: "ACTIVE"
    });
    setTerminalType(null);
    setClickedStates({ xgs: false, agents: false, rlm: false });
    setPhysicalLocation(null);
    dispatch(getTerminalsList(0, null, null, "ACTIVE", null, null));
    dispatch(getOriginalCounts("ACTIVE"));
  };

  useEffect(() => {
    const handler =
      setTimeout(() =>
        onSearchStart(), 1000);
    return () => clearTimeout(handler);
  }, [searchString, onSearchStart]);

  const baseFilters = (
    <div className="terminal-summary__controls">
      <div className="xgs-tracking-filter__bottom-row">
        <div className="terminal-summary__form terminal-summary__item">
          <div className="terminal-summary__controls__double-item">
            <LabeledTextInput
              name="searchString"
              labelMode={LabelModes.column}
              className="terminal-summary__input"
              value={searchString}
              onChange={(e) =>
                setSearchString(e.currentTarget.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  onSearchStart();
                }
              }}
              type="text"
              placeholder="Search Terminals by name, number, manager..."
            />
          </div>
          <div className="terminal-summary__controls__double-item">
            <LabeledSelectInput
              onValueChange={onRegionChanged}
              name="region"
              value={region}
              async
              options={getRegionsOptions()}
              labelMode={LabelModes.column}
              className="terminal-summary__input"
              placeholder="All Regions"
            />
          </div>
          <div className="terminal-summary__controls__double-item">
            <LabeledSelectInput
              onValueChange={onStatusChanged}
              name="status"
              value={status}
              async
              options={statusOptions}
              labelMode={LabelModes.column}
              className="terminal-summary__input"
              placeholder="Status"
            />
          </div>
          <Button
            theme={ButtonThemes.gray}
            onClick={clear}
            disabled={!region && !searchString && status?.value !== "INACTIVE" && !terminalType && !physicalLocation}
            type="button"
          >
            Clear Filters
          </Button>
          <Button
            theme={ButtonThemes.blue}
            onClick={onSearchStart}
            spinner={terminalSummaryState.requestStarted && terminalSummaryState.requestCreator === "GET_TERMINALS"}
          >
            Search
          </Button>
        </div>
        {UserUtils.isXGSAdministrator(userState.profile) &&
          <Button
            theme={ButtonThemes.blue}
            onClick={() => {
              if (terminalDetailsState.data) {
                dispatch(resetTerminalDetails());
              }
              setSlideOpenAddTerminal(true)
            }}
          >
            + Add Terminal
          </Button>
        }
      </div>
    </div>
  );

  const handleViewTerminalDetails = (terminalNumber: number) => {
    history.push(`/terminals/list/${terminalNumber}`);
  };

  const markers: LeafletMarkerModel[] = terminalSummaryState?.terminallist.map(location => {
    const isAgentTerminal = location.terminalType?.type === "Agent Terminal";
    const isWGLocation = location.terminalType?.type === "WG Locations";
    const isXGSTerminal = location.terminalType?.type === "XGS Terminal";


    const facilityTypeKeys = Object.entries(location.facilityTypes)
      .filter(([_, value]) => value)
      .map(([key]) => key);

    return {
      id: location.terminalNumber.toString(),
      kind: "others",
      lat: location.address?.latitude ?? 0,
      lng: location.address?.longitude ?? 0,
      customSvg: isAgentTerminal
        ? {
          component: TerminalMarker,
          props: { color: "#7936D0", width: 36, height: 48 }
        }
        : isWGLocation ? {
          component: TerminalMarker,
          props: { color: "green", width: 36, height: 48 }
        } : isXGSTerminal ? generateFacilityPinSvg(facilityTypeKeys) : {
          component: TerminalMarker,
          props: { color: "#808080", width: 36, height: 48 }
        },
    };
  });

  return (
    <>
      <ContentContainer
        titleComponent={
          <ContentContainerToolbar
            pageId={PAGE_IDS.TERMINAL_SUMMARY}
            title="Manage Terminals"
            className="terminal-summary"
          >
            <div className="terminal-summary__terminal-activitylog ">
              <Button
                theme={ButtonThemes.transparent}
                size={ButtonSizes.auto}
                onClick={() => setActivityLogId("true")}
              >
                <div className="xgs-btn__icon-container">
                  <TerminalActivityIcon className="xgs-btn__icon" />
                </div>
                Terminal Activity Log
              </Button>
            </div>
          </ContentContainerToolbar>
        }
      >
        <>
          <Collapsible
            displayLabel={true}
            header={<div className="terminal-summary__map__container">Terminal Overview</div>}
          >
            <div style={{ width: "100%" }}>
              <div className="terminal-summary__marker__container">
                <div className="terminal-summary__marker__title">Facility Types</div>
                <div className="terminal-summary__marker">

                  <div className="terminal-summary__marker__item">
                    <img src={starWhite} alt="marker" />
                    <span>Consolidation Facility</span>
                  </div>
                  <div className="terminal-summary__marker__item">
                    <img src={markerLightBlue} alt="marker" />
                    <span>Distribution Facility</span>
                  </div>
                  <div className="terminal-summary__marker__item">
                    <img src={markerDarkBlue} alt="marker" />
                    <span>Warehousing</span>
                  </div>
                  <div className="terminal-summary__marker__item">
                    <img src={marker} alt="marker" />
                    <span>Cut Facility</span>
                  </div>
                  <div className="terminal-summary__marker__item">
                    <img src={markerYellow} alt="marker" />
                    <span>Outbound Facility</span>
                  </div>

                </div>
              </div>
              <div className="xgs-map__container">
                <XGSLeafletMap
                  markers={markers}
                  renderPopup={(id, kind) => <TerminalSummaryInfo info={{
                    terminalName: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.terminalName,
                    terminalNumber: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.terminalNumber,
                    airportCode: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.airportCode,
                    city: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.address?.city,
                    stateProvince: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.address?.stateProvince,
                    physicalAddress: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.address?.physicalAddress,
                    zipPostalCode: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.address?.zipPostalCode,
                    phoneNumber: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.contact?.phoneNumber,
                    status: terminalSummaryState?.terminallist.find((location) => location.terminalNumber.toString() === id)?.status,
                  }} />}
                  overlay={<div className="xgs-map__informationbox">
                    <strong>Terminal Types</strong>
                    {terminalTypes.map(({ key, label, tooltip }) => (
                      <div key={key} className={`xgs-map__informationbox__infodata xgs-map__informationbox__infodata--${key}`}>
                        <span></span>
                        {label}
                        <Popover values={<div className="terminal-summary__tooltip">{tooltip}</div>} />
                      </div>
                    ))}
                  </div>}
                  useClustering={false}
                  defaultZoom={4}
                />
              </div>
            </div>
          </Collapsible>
          {baseFilters}
          <div className="xgs-list__summary">
            <TerminalSummaryCount
              terminalSummary={originalCountsState.data}
              region={terminalTileLoadingStatusState.summary.regionCount}
              clickedStates={clickedStates}
              setClickedStates={setClickedStates}
              isLoading={terminalTileLoadingStatusState.loading || originalCountsState.isLoading}
              handleTerminals={(type) => {
                setTerminalType(type);
                setPhysicalLocation(true);
                dispatch(getTerminalsList(0, searchString, region?.label, status?.value, type, true));
              }}
              handleClear={() => {
                setPhysicalLocation(false)
                dispatch(getTerminalsList(0, searchString, region?.label, status?.value, null, false));
              }}
            />
          </div>
          {!terminalSummaryState.loadingFailed && (
            <div className="terminal-summary__orders">
              <Table
                isLoading={terminalSummaryState.requestStarted && (terminalSummaryState.requestCreator === "GET_TERMINALS" || terminalSummaryState.requestCreator === "GET_TERMINALS_PORTION")}
                columns={getTerminalSummaryColumns()}
                data={terminalSummaryState.terminallist}
                noResultsText="There are no records available"
                infiniteScroll
                infiniteScrollLoading={terminalSummaryState.fetchPortionStarted && terminalSummaryState.requestCreator === "GET_TERMINALS_PORTION"}
                infiniteScrollHasNext={!terminalSummaryState.terminalSummaryRequest.last}
                onInfiniteScroll={onInfiniteScroll}
                responsive={true}
                sorting={true}
                filters={true}
                minTableHeight={420}
                cursorPointer={true}
                onRowClicked={(row: any) => handleViewTerminalDetails(row.terminalNumber)}
                autoResetSelectedRows={true}
              />
            </div>
          )}
          {terminalSummaryState.loadingFailed && (
            <XGSErrorMessage>{terminalSummaryState.error || "Error while obtaining the list of shipments"}</XGSErrorMessage>
          )}
          <TerminalActivityLog
            show={!!activityLogId}
            onClose={() => {
              setActivityLogId(undefined);
            }}
          />
        </>
      </ContentContainer>
      <AddEditTerminal isEdit={false} handleSuccess={() => { dispatch(getTerminalsList()); dispatch(getTerminalTilesLoadingStatus()); toast.info("Terminal added successfully"); }} slideOpen={slideOpenAddTerminal} slideClose={() => {
        setSlideOpenAddTerminal(false);
      }} />
    </>
  );
});

export default TerminalSummary;
